import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { X, Clock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BookingDetail } from '../../model/professional/booking-model';
import { reservationViewModel } from '../../view-model/professional/reservation-view-model';
import companyStore from '../../store/company-store';
import Button from '../ui/button';
import InputWithIcon from '../generics/input-with-icon';
import PromoCodeInput from '../client/promo-code-input';
import CompactDatePicker from '../ui/compact-date-picker';

interface EditManualBookingFormProps {
  booking: BookingDetail;
  onSuccess: () => void;
  onCancel: () => void;
}

const EditManualBookingForm: React.FC<EditManualBookingFormProps> = observer(({
  booking,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation();
  const viewModel = reservationViewModel;
  const [isLoadingServices, setIsLoadingServices] = useState(true);

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoadingServices(true);
      await viewModel.loadServices();
      viewModel.loadBookingForEdit(booking);
      setIsLoadingServices(false);
    };

    initializeForm();

    return () => {
      viewModel.resetForm();
    };
  }, [booking]);

  // Recalculer l'heure de fin quand la durée totale ou l'heure de début change
  useEffect(() => {
    if (viewModel.manualStartTime && viewModel.totalDuration > 0) {
      const [hours, minutes] = viewModel.manualStartTime.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);
      const endDate = new Date(startDate.getTime() + viewModel.totalDuration * 60000);
      const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
      viewModel.setManualEndTime(endTime);
    }
  }, [viewModel.totalDuration, viewModel.selectedServices.length, viewModel.manualStartTime]);

  const handleSubmit = async () => {
    const success = await viewModel.submitForm();
    if (success) {
      onSuccess();
    }
  };

  // Calculer le prix d'un item du panier
  const calculateItemPrice = (item: any) => {
    const servicePrice = typeof item.service.price === 'number'
      ? item.service.price
      : parseFloat(String(item.service.price || 0));

    let optionsPrice = 0;
    if (item.service.optionCategories) {
      for (const category of item.service.optionCategories) {
        if (category.options) {
          for (const option of category.options) {
            if (item.optionIds.includes(option.id)) {
              const optionPrice = typeof option.price === 'number'
                ? option.price
                : parseFloat(String(option.price || 0));
              optionsPrice += optionPrice;
            }
          }
        }
      }
    }

    return servicePrice + optionsPrice;
  };

  // Calculer la durée d'un item du panier
  const calculateItemDuration = (item: any) => {
    const serviceDuration = item.service.duration || 0;
    let optionsDuration = 0;

    if (item.service.optionCategories) {
      for (const category of item.service.optionCategories) {
        if (category.options) {
          for (const option of category.options) {
            if (item.optionIds.includes(option.id) && option.durationMinutes) {
              optionsDuration += option.durationMinutes;
            }
          }
        }
      }
    }

    return serviceDuration + optionsDuration;
  };

  // Obtenir le nom d'une option par son ID
  const getOptionName = (item: any, optionId: string): string => {
    if (!item.service.optionCategories) return '';

    for (const category of item.service.optionCategories) {
      if (category.options) {
        const option = category.options.find((opt: any) => opt.id === optionId);
        if (option) return option.name;
      }
    }
    return '';
  };

  const collaborators = companyStore.selectedCompany?.collaboratorCompanies || [];
  const hasOnlyOneCollaborator = collaborators.length === 1;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Contenu avec défilement */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
        <div className="space-y-4 sm:space-y-5 md:space-y-6">

          {/* Services sélectionnés */}
          {viewModel.selectedServices.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('booking.selectedServices', 'Services sélectionnés')} ({viewModel.selectedServices.length})
                </h3>
              </div>

              <div className="space-y-2">
                {viewModel.selectedServices.map((item, index) => (
                  <div
                    key={index}
                    className="relative bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                  >
                    {/* Bouton supprimer */}
                    <button
                      onClick={() => viewModel.removeServiceFromBooking(index)}
                      className="absolute top-2 right-2 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
                      title={t('booking.removeService', 'Retirer le service') as string}
                    >
                      <X size={16} />
                    </button>

                    {/* Nom du service */}
                    <div className="pr-8">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm mb-1">
                        {item.service.name}
                      </h4>

                      {/* Options */}
                      {item.optionIds.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {item.optionIds.map((optionId: string, optIndex: number) => {
                            const optionName = getOptionName(item, optionId);
                            return optionName ? (
                              <div
                                key={optIndex}
                                className="flex items-center text-xs text-gray-600 dark:text-gray-400"
                              >
                                <span>+ {optionName}</span>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}

                      {/* Durée et prix */}
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-primary/20">
                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          <Clock size={12} />
                          <span>{calculateItemDuration(item)} min</span>
                        </div>
                        <div className="text-sm font-bold text-primary">
                          {calculateItemPrice(item).toFixed(2)}€
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ajouter un autre service */}
          {!viewModel.selectedService && !isLoadingServices && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('booking.addAnotherService', 'Ajouter un autre service')}
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {viewModel.services
                  .filter((service: any) => service.isActive !== false)
                  .map((service: any) => (
                    <div
                      key={service.id}
                      onClick={() => viewModel.selectAndAddService(service.id)}
                      className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hover:border-primary cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm mb-1">
                            {service.name}
                          </h4>
                          {service.description && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                              {service.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            {service.duration && (
                              <span>{service.duration} min</span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm font-bold text-primary ml-3">
                          {typeof service.price === 'number' ? service.price.toFixed(2) : service.price}€
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Sélection des options du service en cours */}
          {viewModel.selectedService && viewModel.optionCategories.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('booking.selectOptions', 'Options pour')} {viewModel.serviceDetails?.name}
              </h3>
              {viewModel.optionCategories.map((category: any) => (
                <div key={category.id} className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {category.name}
                    {category.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {category.allowMultiple ? (
                    // Checkboxes pour options multiples
                    <div className="space-y-2">
                      {category.options.map((option: any) => (
                        <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={viewModel.selectedOptions[category.id]?.includes(option.id) || false}
                            onChange={(e) => viewModel.setOptionCheckboxValue(category.id, option.id, e.target.checked)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {option.name} (+{typeof option.price === 'number' ? option.price.toFixed(2) : option.price}€)
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    // Radio buttons pour option unique
                    <div className="space-y-2">
                      {category.options.map((option: any) => (
                        <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={category.id}
                            checked={viewModel.selectedOptions[category.id]?.[0] === option.id}
                            onChange={() => viewModel.setOptionSelectValue(category.id, option.id)}
                            className="border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {option.name} (+{typeof option.price === 'number' ? option.price.toFixed(2) : option.price}€)
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex gap-2">
                <Button
                  onClick={() => viewModel.addServiceToBooking()}
                  className="flex-1 bg-primary text-white hover:bg-primary-dark"
                >
                  {t('booking.addToCart', 'Ajouter au panier')}
                </Button>
                <Button
                  onClick={() => viewModel.setSelectedService('')}
                  variant="outline"
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
              </div>
            </div>
          )}

          {/* Informations de réservation */}
          {!viewModel.selectedService && viewModel.selectedServices.length > 0 && (
            <>
              {/* Collaborateur - uniquement si plusieurs collaborateurs */}
              {!hasOnlyOneCollaborator && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('booking.collaborator', 'Collaborateur')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    value={viewModel.selectedCollaboratorCompagnieId}
                    onChange={(e) => viewModel.setSelectedCollaboratorCompagnieId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-gray-200"
                  >
                    <option value="">{t('booking.selectCollaborator', 'Sélectionner un collaborateur')}</option>
                    {collaborators.map((collab: any) => (
                      collab.collaborator ? (
                        <option key={collab.collaboratorCompagnieId} value={collab.collaboratorCompagnieId}>
                          {collab.collaborator.firstName} {collab.collaborator.lastName}
                        </option>
                      ) : null
                    ))}
                  </select>
                </div>
              )}

              {/* Date */}
              <div>
                <CompactDatePicker
                  label={t('booking.date', 'Date') || 'Date'}
                  id="selectedDate"
                  name="selectedDate"
                  value={viewModel.selectedDate}
                  onChange={(e) => viewModel.setSelectedDate(e.target.value)}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Horaires */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <InputWithIcon
                    label={t('booking.startTime', 'Heure de début')}
                    id="manualStartTime"
                    name="manualStartTime"
                    type="time"
                    value={viewModel.manualStartTime}
                    onChange={(e) => {
                      viewModel.setManualStartTime(e.target.value);
                      // Calculer automatiquement l'heure de fin
                      if (e.target.value && viewModel.totalDuration > 0) {
                        const [hours, minutes] = e.target.value.split(':').map(Number);
                        const startDate = new Date();
                        startDate.setHours(hours, minutes, 0, 0);
                        const endDate = new Date(startDate.getTime() + viewModel.totalDuration * 60000);
                        const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
                        viewModel.setManualEndTime(endTime);
                      }
                    }}
                    icon={<Clock size={16} />}
                    required
                  />
                </div>
                <div>
                  <InputWithIcon
                    label={t('booking.endTime', 'Heure de fin (calculée)')}
                    id="manualEndTime"
                    name="manualEndTime"
                    type="time"
                    value={viewModel.manualEndTime}
                    icon={<Clock size={16} />}
                    disabled
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('booking.calculatedFromDuration', 'Calculée automatiquement selon la durée totale')}
                  </p>
                </div>
              </div>

              {/* Informations client */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('booking.clientInformation', 'Informations client')}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <InputWithIcon
                    label={t('booking.firstName', 'Prénom')}
                    id="clientFirstName"
                    name="clientFirstName"
                    value={viewModel.clientFirstName}
                    onChange={(e) => viewModel.setClientFirstName(e.target.value)}
                    icon={<User size={16} />}
                    required
                  />
                  <InputWithIcon
                    label={t('booking.lastName', 'Nom')}
                    id="clientLastName"
                    name="clientLastName"
                    value={viewModel.clientLastName}
                    onChange={(e) => viewModel.setClientLastName(e.target.value)}
                    icon={<User size={16} />}
                    required
                  />
                </div>

                <InputWithIcon
                  label={t('booking.email', 'Email')}
                  id="clientEmail"
                  name="clientEmail"
                  type="email"
                  value={viewModel.clientEmail}
                  onChange={(e) => viewModel.setClientEmail(e.target.value)}
                  icon={<User size={16} />}
                  required
                />

                <InputWithIcon
                  label={t('booking.phone', 'Téléphone')}
                  id="clientPhone"
                  name="clientPhone"
                  type="tel"
                  value={viewModel.clientPhone}
                  onChange={(e) => viewModel.setClientPhone(e.target.value)}
                  icon={<User size={16} />}
                />
              </div>

              {/* Code promo */}
              <div className="mt-4">
                <PromoCodeInput
                  promoCode={viewModel.promoCode}
                  isValidating={viewModel.isValidatingPromoCode}
                  isValid={viewModel.promoCodeValidated}
                  discountAmount={viewModel.promoCodeDiscount}
                  validationMessage={viewModel.promoCodeMessage}
                  onPromoCodeChange={(code) => viewModel.setPromoCode(code)}
                  onValidate={() => viewModel.validatePromoCode()}
                  onRemove={() => viewModel.resetPromoCode()}
                  enableDarkMode={true}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pied de page avec total et boutons */}
      {!viewModel.selectedService && viewModel.selectedServices.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-900 pt-3 sm:pt-4 border-t border-gray-100 dark:border-gray-700 mt-3 sm:mt-4 pb-4 sm:pb-5 md:pb-6 px-3 sm:px-4">
          <div className="bg-gradient-to-r from-gray-50/80 to-white/80 dark:from-gray-800/80 dark:to-gray-900/80 backdrop-blur-sm p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <div className="w-full sm:w-auto text-center sm:text-left">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  {t('booking.totalPrice', 'Prix total')}
                </span>
                {viewModel.promoCodeValidated && viewModel.promoCodeDiscount > 0 ? (
                  <>
                    <p className="text-lg text-gray-500 dark:text-gray-400 line-through">
                      {viewModel.totalPrice.toFixed(2)} €
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {viewModel.finalPrice.toFixed(2)} €
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-primary">
                    {viewModel.totalPrice.toFixed(2)} €
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('booking.totalDuration', 'Durée totale')}: {viewModel.totalDuration} min
                </p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  onClick={onCancel}
                  variant="outline"
                  className="flex-1 sm:flex-none"
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!viewModel.isFormValid() || viewModel.isSubmitting}
                  className="flex-1 sm:flex-none px-6"
                >
                  {viewModel.isSubmitting
                    ? t('common.saving', 'Enregistrement...')
                    : t('common.save', 'Enregistrer')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default EditManualBookingForm;
